import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection, AnimatedHeading } from "@/components/ui/AnimatedSection";
import { Users, Heart, Award, Clock } from "lucide-react";
import heroImg from "@/assets/rewe-aussenaufnahme.webp";
import logo from "@/assets/logo.webp";
import herrGahndiImg from "@/assets/herr-gahndi.webp";
import { SEO } from "@/components/SEO";

const inhaberPaarImg = "/lovable-uploads/inhaber-paar-neu.webp";
const frauGahndiImg = "/lovable-uploads/frau-gahndi-neu.webp";
const teamAlleHeroImg = "/lovable-uploads/team-alle-clean.webp";
const teamAlleV2Img = "/lovable-uploads/team-alle-v2.webp";
const marktleiterImg = "/lovable-uploads/marktleiter-neu.webp";
const leitungFrischethekenImg = "/lovable-uploads/leitung-frischetheke-neu.webp";
const leitungGetraenkeImg = "/lovable-uploads/leitung-getraenke-neu.webp";
const stellvLeitungGetraenkeImg = "/lovable-uploads/stellv-leitung-getraenke.webp";
const leitungMolkereiImg = "/lovable-uploads/leitung-molkerei-neu.webp";
const leitungObstGemueseImg = "/lovable-uploads/leitung-obst-gemuese-neu.webp";
const azubiImg = "/lovable-uploads/azubi.webp";
const mitarbeiterinFleischthekeImg = "/lovable-uploads/mitarbeiterin-fleischtheke.webp";

const values = [
  {
    icon: <Heart className="w-8 h-8" />,
    title: "Leidenschaft",
    description: "Wir lieben, was wir tun – und das schmeckt man bei jedem Einkauf.",
  },
  {
    icon: <Award className="w-8 h-8" />,
    title: "Qualität",
    description: "Nur das Beste für unsere Kunden – frisch, regional und sorgfältig ausgewählt.",
  },
  {
    icon: <Users className="w-8 h-8" />,
    title: "Gemeinschaft",
    description: "Wir sind mehr als ein Supermarkt – wir sind Teil der Nachbarschaft.",
  },
  {
    icon: <Clock className="w-8 h-8" />,
    title: "Zuverlässigkeit",
    description: "Jeden Tag für dich da – mit konstantem Service und vollem Sortiment.",
  },
];

const teamMembers = [
  {
    image: marktleiterImg,
    name: "Marktleiter",
    role: "Organisation & Planung",
  },
  {
    image: leitungFrischethekenImg,
    name: "Abteilungsleitung",
    role: "Frischetheke & Fleischerei",
  },
  {
    image: leitungGetraenkeImg,
    name: "Abteilungsleitung",
    role: "Getränkemarkt",
  },
  {
    image: stellvLeitungGetraenkeImg,
    name: "Stellvertretende Leitung",
    role: "Getränkemarkt",
  },
  {
    image: leitungMolkereiImg,
    name: "Abteilungsleitung",
    role: "Molkereiprodukte",
  },
  {
    image: leitungObstGemueseImg,
    name: "Mitarbeiterin",
    role: "Freshcut",
  },
  {
    image: mitarbeiterinFleischthekeImg,
    name: "Mitarbeiterin",
    role: "Frischetheke",
  },
  {
    image: azubiImg,
    name: "Auszubildender",
    role: "Ausbildung im Markt",
  },
];

export default function UeberUns() {
  return (
    <Layout>
      <SEO title="Über uns | REWE V. Gahndi Flörsheim-Weilbach" description="Lerne die Familie Gahndi und unser Team kennen – persönlicher Service seit vielen Jahren in Flörsheim-Weilbach." path="/ueber-uns" />
      {/* Hero */}
      <section className="relative min-h-[65vh] flex items-start justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-[center_5%] bg-no-repeat"
          style={{ backgroundImage: `url(${leitungFrischethekenImg})` }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative container-custom pt-16 pb-20 md:pt-20">
          <div className="-translate-y-[20%]">
          <AnimatedHeading as="h1" subtitle="Über uns" hideDivider>
            <span className="block">Willkommen bei</span>
            <img
              src={logo}
              alt="REWE V. Gahndi"
              className="mx-auto -mt-1 md:-mt-2 h-28 md:h-40 lg:h-48 w-auto translate-y-[105%]"
            />
          </AnimatedHeading>
          </div>
          <AnimatedSection>
            <p className="text-center text-muted-foreground max-w-3xl mx-auto text-lg translate-y-[130%]">
              Dein Vollsortimenter in Flörsheim-Weilbach mit Herz, Qualität und regionalem Fokus.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Store Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative overflow-hidden rounded-2xl aspect-[4/3]"
              >
                <img 
                  src={heroImg} 
                  alt="REWE V. Gahndi Außenansicht"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-6 text-center lg:text-left">
                Unser <span className="text-gold-gradient">Markt</span>
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed text-center lg:text-left">
                <p>
                  Herzlich willkommen bei REWE V. Gahndi in Flörsheim-Weilbach! Auf einer großen 
                  Fläche steht dir ein reichhaltiges Angebot an allen Waren des täglichen Bedarfs 
                  zur Verfügung.
                </p>
                <p>
                  Als Vollsortimenter versorgen wir unsere Kunden mit köstlichen Frischeprodukten, 
                  einer heißen Theke, reichhaltigen Molkereiwaren, einer üppigen Auswahl an Getränken 
                  sowie einer Vielzahl an weiteren Highlights.
                </p>
                <p>
                  Ganz besonders sticht unsere eigene Fleischerei heraus, in der viele hochwertige 
                  Produkte in eigener Herstellung produziert werden. Ebenso findest du direkt am 
                  Markt die Bäckerei Schäfer mit frischen Backwaren.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Owners Section */}
      <section className="section-padding bg-secondary">
        <div className="container-custom">
          <AnimatedHeading subtitle="Die Inhaber">
            Familie <span className="text-gold-gradient">Gahndi</span>
          </AnimatedHeading>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <AnimatedSection>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative overflow-hidden rounded-2xl aspect-[4/3]"
              >
                <img 
                  src={inhaberPaarImg} 
                  alt="Inhaber Vigheshan und Nilam Gahndi" 
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="space-y-4 text-muted-foreground leading-relaxed text-center lg:text-left">
                <p>
                  Hinter REWE V. Gahndi steht ein Familienunternehmen mit Herz und Leidenschaft. 
                  Vigheshan und Nilam Gahndi führen den Markt mit großem Engagement und persönlichem 
                  Einsatz.
                </p>
                <p>
                  Für sie ist der Markt mehr als nur ein Geschäft – es ist ein Ort der Begegnung, 
                  an dem Kunden zu Freunden werden. Die familiäre Atmosphäre spürt man in jedem 
                  Bereich des Marktes.
                </p>
                <p>
                  Mit ihrem Team sorgen sie täglich dafür, dass du ein erstklassiges Einkaufserlebnis 
                  genießen kannst – geprägt von Qualität, Frische und herzlichem Service.
                </p>
              </div>
            </AnimatedSection>
          </div>

          {/* Owner portraits */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <AnimatedSection delay={0.1}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="card-premium overflow-hidden"
              >
                <div className="aspect-[4/3] overflow-hidden rounded-xl mb-4">
                  <img 
                    src={herrGahndiImg} 
                    alt="Vigheshan Gahndi - Inhaber" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-heading font-semibold text-center">Vigheshan Gahndi</h3>
                <p className="text-primary text-sm text-center">Inhaber</p>
              </motion.div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="card-premium overflow-hidden"
              >
                <div className="aspect-[4/3] overflow-hidden rounded-xl mb-4">
                  <img 
                  src={frauGahndiImg} 
                    alt="Nilam Gahndi - Inhaberin" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-heading font-semibold text-center">Nilam Gahndi</h3>
                <p className="text-primary text-sm text-center">Inhaberin</p>
              </motion.div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Full Team Photo */}
      <section className="section-padding">
        <div className="container-custom">
          <AnimatedHeading subtitle="Gemeinsam stark">
            Unser <span className="text-gold-gradient">Team</span>
          </AnimatedHeading>

          <AnimatedSection delay={0.2}>
            <div className="max-w-3xl mx-auto text-center space-y-4 text-muted-foreground leading-relaxed mb-16">
              <p>
                Das Team hinter unserem REWE Markt besteht aus engagierten Mitarbeiterinnen und 
                Mitarbeitern, die jeden Tag ihr Bestes geben, um dir ein angenehmes Einkaufserlebnis 
                zu bieten.
              </p>
              <p>
                Wir pflegen einen familiären Umgang – untereinander und mit unseren Kunden. Bei 
                uns bist du nicht nur ein Kunde, sondern ein Gast. Wir kennen viele unserer 
                Stammkunden persönlich und freuen uns immer über ein freundliches Gespräch.
              </p>
            </div>
          </AnimatedSection>

          {/* Team Members Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teamMembers.map((member, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="card-premium overflow-hidden group"
                >
                  <div className="aspect-[4/3] overflow-hidden rounded-xl mb-4">
                    <img 
                      src={member.image} 
                      alt={`${member.name} - ${member.role}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-center">{member.name}</h3>
                  <p className="text-primary text-sm text-center">{member.role}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-secondary">
        <div className="container-custom">
          <AnimatedHeading subtitle="Unsere Werte">
            Wofür wir <span className="text-gold-gradient">stehen</span>
          </AnimatedHeading>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <AnimatedSection key={value.title} delay={index * 0.1}>
                <div className="card-premium text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-3">{value.title}</h3>
                  <p className="text-muted-foreground text-sm">{value.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="section-padding">
        <div className="container-custom">
          <AnimatedHeading subtitle="Unser Standort">
            Flörsheim-<span className="text-gold-gradient">Weilbach</span>
          </AnimatedHeading>

          <AnimatedSection>
            <div className="card-premium text-center p-8 max-w-2xl mx-auto">
              <p className="text-muted-foreground leading-relaxed mb-6">
                Du findest uns im Herzen von Flörsheim-Weilbach, direkt an der Industriestraße. 
                Mit 115 kostenlosen Parkplätzen ist ein stressfreier Einkauf garantiert.
              </p>
              <div className="space-y-2 text-sm mb-6">
                <p className="font-medium">Industriestraße 40</p>
                <p className="text-muted-foreground">65439 Flörsheim-Weilbach</p>
                <p className="text-muted-foreground">Mo. – Sa.: 7:00 – 22:00 Uhr</p>
              </div>
              <a
                href="https://goo.gl/maps/VJQynYQHgqsTnpz99"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold-filled"
              >
                Route planen
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
